import type {
  RunState,
  RoomData,
  GuessResult,
  RoomCompletionResult,
  HintWithMetadata,
  PowerUpItem,
  RoomModifier,
} from '@crackpoint/shared/types';
import type { GameAdapter } from './GameAdapter';
import { generateId } from '@crackpoint/shared/utils';

interface ApiHintData {
  digits: number[];
  correct_placed: number;
  correct_misplaced: number;
  rarity: string | null;
  power_up: string | null;
  percentile: number;
}

interface ApiModifierData {
  id: string;
  name: string;
  description: string;
}

interface ApiPowerUpData {
  type: string;
  tier: string;
  digits: number[];
  correct_placed: number;
  correct_misplaced: number;
}

interface ApiRunResponse {
  id: string;
  lives_remaining: number;
  rooms_cleared: number;
  current_streak: number;
  highest_streak: number;
  perfect_streak: number;
  highest_perfect_streak: number;
  total_stars: number;
  three_star_count: number;
  current_room_number: number;
  active_modifier_id: string | null;
  inventory: PowerUpItem[];
  state: string;
}

interface ApiRoomResponse {
  id: string;
  room_number: number;
  code_length: number;
  base_hints: ApiHintData[];
  reserve_hint_count: number;
  modifier: ApiModifierData;
  power_ups: ApiPowerUpData[];
  hidden_hint_index: number | null;
  hidden_revealed: boolean;
}

interface ApiGuessResponse {
  correct_placed: number;
  correct_misplaced: number;
  solved: boolean;
  lives_remaining?: number;
  hidden_hint_revealed?: ApiHintData | null;
  run_ended?: boolean;
  stars_earned?: number;
  loot?: Record<string, unknown> | null;
  bonus_lives?: number;
  run_state?: Record<string, unknown>;
  next_room_modifier?: string;
  optimal_hint_count?: number;
}

export class ServerAdapter implements GameAdapter {
  private readonly baseUrl: string;
  private readonly authToken?: string;
  private runId: string | null = null;
  private runState: RunState | null = null;
  private roomData: RoomData | null = null;
  private completionData: RoomCompletionResult | null = null;

  constructor(baseUrl: string, authToken?: string) {
    this.baseUrl = baseUrl;
    this.authToken = authToken;
  }

  async createRun(): Promise<RunState> {
    const response = await this._request<ApiRunResponse>('POST', '/api/v1/runs');
    this.runId = response.id;
    this.runState = this._mapRunResponse(response);
    return this.runState;
  }

  async generateRoom(): Promise<RoomData> {
    if (!this.runId) {
      throw new Error('No active run');
    }

    const response = await this._request<ApiRoomResponse>(
      'POST',
      `/api/v1/runs/${this.runId}/room`,
    );

    this.roomData = this._mapRoomResponse(response);
    this.completionData = null;
    return this.roomData;
  }

  async submitGuess(guess: number[]): Promise<GuessResult> {
    if (!this.runId) {
      throw new Error('No active run');
    }

    const response = await this._request<ApiGuessResponse>(
      'POST',
      `/api/v1/runs/${this.runId}/room/guess`,
      { guess },
    );

    if (response.solved) {
      if (response.run_state) {
        this.runState = {
          id: this.runId,
          lives_remaining: (response.run_state as Record<string, number>).lives_remaining ?? this.runState?.lives_remaining ?? 0,
          rooms_cleared: (response.run_state as Record<string, number>).rooms_cleared ?? this.runState?.rooms_cleared ?? 0,
          current_streak: (response.run_state as Record<string, number>).current_streak ?? 0,
          highest_streak: (response.run_state as Record<string, number>).highest_streak ?? 0,
          total_stars: (response.run_state as Record<string, number>).total_stars ?? 0,
          current_room_number: this.runState?.current_room_number ?? 0,
          inventory: this.runState?.inventory ?? [],
          state: 'active',
        };
      }

      const lootData = response.loot as Record<string, unknown> | null;
      const loot: PowerUpItem | null = lootData
        ? {
            type: (lootData.type as string) as 'oracle' | 'surge' | 'boost',
            tier: (lootData.tier as string) ?? '',
            digits: (lootData.digits as number[]) ?? [],
            correct_placed: (lootData.correct_placed as number) ?? 0,
            correct_misplaced: (lootData.correct_misplaced as number) ?? 0,
          }
        : null;

      this.completionData = {
        stars_earned: response.stars_earned ?? 0,
        optimal_hint_count: response.optimal_hint_count ?? 0,
        loot,
        bonus_lives: response.bonus_lives ?? 0,
        run_state: this.runState!,
        next_room_modifier: {
          id: (response.next_room_modifier ?? 'clean') as RoomModifier['id'],
          name: '',
          description: '',
        },
      };

      return {
        solved: true,
        correct_placed: response.correct_placed,
        correct_misplaced: response.correct_misplaced,
        lives_remaining: this.runState?.lives_remaining ?? 0,
        hidden_hint_revealed: null,
        run_ended: false,
      };
    }

    if (this.runState) {
      this.runState = {
        ...this.runState,
        lives_remaining: response.lives_remaining ?? this.runState.lives_remaining,
      };
    }

    let hiddenHintRevealed: HintWithMetadata | null = null;
    if (response.hidden_hint_revealed) {
      hiddenHintRevealed = this._mapHintData(response.hidden_hint_revealed);
    }

    return {
      solved: false,
      correct_placed: response.correct_placed,
      correct_misplaced: response.correct_misplaced,
      lives_remaining: response.lives_remaining ?? 0,
      hidden_hint_revealed: hiddenHintRevealed,
      run_ended: response.run_ended ?? false,
    };
  }

  async drawReserveHint(): Promise<HintWithMetadata | null> {
    if (!this.runId) {
      throw new Error('No active run');
    }

    const response = await this._request<ApiHintData | null>(
      'POST',
      `/api/v1/runs/${this.runId}/room/reserve-hint`,
    );

    if (!response) {
      return null;
    }

    return this._mapHintData(response);
  }

  async usePowerUp(index: number): Promise<PowerUpItem> {
    if (!this.runId) {
      throw new Error('No active run');
    }

    const response = await this._request<ApiPowerUpData>(
      'POST',
      `/api/v1/runs/${this.runId}/room/power-up`,
      { power_up_index: index },
    );

    return {
      type: response.type as 'oracle' | 'surge' | 'boost',
      tier: response.tier,
      digits: response.digits,
      correct_placed: response.correct_placed,
      correct_misplaced: response.correct_misplaced,
    };
  }

  async completeRoom(): Promise<RoomCompletionResult> {
    if (this.completionData) {
      return this.completionData;
    }

    if (!this.runId) {
      throw new Error('No active run');
    }

    const response = await this._request<ApiRunResponse>(
      'GET',
      `/api/v1/runs/${this.runId}`,
    );

    this.runState = this._mapRunResponse(response);

    throw new Error('No completion data available');
  }

  getRunState(): RunState | null {
    return this.runState;
  }

  getRoomData(): RoomData | null {
    return this.roomData;
  }

  private _mapRunResponse(response: ApiRunResponse): RunState {
    return {
      id: response.id,
      lives_remaining: response.lives_remaining,
      rooms_cleared: response.rooms_cleared,
      current_streak: response.current_streak,
      highest_streak: response.highest_streak,
      total_stars: response.total_stars,
      current_room_number: response.current_room_number,
      inventory: response.inventory ?? [],
      state: response.state as RunState['state'],
    };
  }

  private _mapRoomResponse(response: ApiRoomResponse): RoomData {
    return {
      room_number: response.room_number,
      code_length: response.code_length,
      base_hints: response.base_hints.map((h) => this._mapHintData(h)),
      reserve_hint_count: response.reserve_hint_count,
      modifier: {
        id: response.modifier.id as RoomModifier['id'],
        name: response.modifier.name,
        description: response.modifier.description,
      },
      power_ups: response.power_ups.map((p) => ({
        type: p.type as 'oracle' | 'surge' | 'boost',
        tier: p.tier,
        digits: p.digits,
        correct_placed: p.correct_placed,
        correct_misplaced: p.correct_misplaced,
      })),
      hidden_hint_index: response.hidden_hint_index,
    };
  }

  private _mapHintData(hint: ApiHintData): HintWithMetadata {
    return {
      id: generateId(),
      digits: hint.digits,
      correct_placed: hint.correct_placed,
      correct_misplaced: hint.correct_misplaced,
      rarity: hint.rarity as HintWithMetadata['rarity'],
      power_up: hint.power_up as HintWithMetadata['power_up'],
      percentile: hint.percentile,
      drawn_at: Date.now(),
      is_new: true,
    };
  }

  private async _request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    const options: RequestInit = { method, headers };

    if (body !== undefined) {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(url, options);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json() as Promise<T>;
  }
}
